// Misc
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import withWidth, { isWidthDown } from '@material-ui/core/withWidth'

// Actions
import connectActions from 'utils/connectActions'
import { unsplashSearch } from 'actions/integrations'

// Atoms
import Fade from '@material-ui/core/Fade'
import Typography from '@material-ui/core/Typography'
import GridList from '@material-ui/core/GridList'
import GridListTile from '@material-ui/core/GridListTile'
import GridListTileBar from '@material-ui/core/GridListTileBar'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Container from 'atoms/Container'
import DesktopOnlyCard from 'atoms/DesktopOnlyCard'
import Spacer from 'atoms/Spacer'
import { AppProgressActivator } from 'atoms/AppProgress'
import ContentLoader from 'atoms/ContentLoader'
import TextInput from 'atoms/TextInput'
import InfiniteScroll from 'atoms/InfiniteScroll'
import Expand from 'atoms/Expand'
import Link from 'atoms/Link'

// Icons
import ErrorIcon from '@material-ui/icons/Error'

// Styles
const styles = theme => ({
  result: {
    cursor: 'pointer',
    '&:hover $resultBar': {
      opacity: 1,
      transform: 'translateY(0)',
    }
  },
  resultImage: {
    outline: 0,
  },
  resultBar: {
    opacity: 0,
    transform: 'translateY(100%)',
    transition: theme.transitions.create(['opacity', 'transform']),
  },
})

const defaultState = {
  page: 1,
  query: '',
  results: null,
  totalPages: 0,
}

const mapActionsToProps = {
  unsplashSearchAction: unsplashSearch,
}

export default
@connectActions(mapActionsToProps)
@withStyles(styles)
@withWidth()
class UnsplashSearch extends Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    unsplashSearchAction: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired,
    onResultSelect: PropTypes.func.isRequired,
    width: PropTypes.string.isRequired,
  }

  state = defaultState

  getResultColsCount() {
    const { width } = this.props
    return isWidthDown('xs', width) ? 2 : 3
  }

  hasMore = () => {
    const { totalPages, page } = this.state
    return totalPages > page
  }

  loadMore = () => {
    const { page } = this.state
    const { unsplashSearchAction } = this.props
    if (unsplashSearchAction.isPending) return
    this.setState({ page: page + 1 }, this.performSearch)
  }

  performSearch = async () => {
    const { unsplashSearchAction } = this.props
    const { query, page, results: stateResults } = this.state
    try {
      const { results, total_pages: totalPages } = await unsplashSearchAction.run({
        params: [{ query, page }],
      })
      this.setState({
        results: [...(stateResults || []), ...results],
        totalPages
      })
    } catch (e) {
      // do nothing
    }
  }

  handleInputSave = (query) => {
    const { unsplashSearchAction } = this.props
    unsplashSearchAction.reset()
    if (query) {
      this.setState({ ...defaultState, query }, this.performSearch)
    }
  }

  handleResultClick = result => () => {
    const { onResultSelect } = this.props
    onResultSelect(result)
  }

  renderInput() {
    return (
      <TextInput
        required
        placeholder="Type any keywords to search for images on Unsplash & hit enter"
        onSave={this.handleInputSave}
      />
    )
  }

  renderLoading() {
    const { results } = this.state
    return (
      <Fragment>
        <Spacer />
        <AppProgressActivator />
        <ContentLoader centered={!!results} />
      </Fragment>
    )
  }

  renderEmptyResult() {
    return (
      <Fragment>
        <Spacer />
        <DesktopOnlyCard>
          <List>
            <ListItem>
              <ListItemIcon>
                <ErrorIcon />
              </ListItemIcon>
              <ListItemText primary="No result found" />
            </ListItem>
          </List>
        </DesktopOnlyCard>
      </Fragment>
    )
  }

  renderResult = (result, index) => {
    const { classes } = this.props
    const { width, height, id, urls, user } = result
    const sizeRatio = width / height
    const totalCols = this.getResultColsCount()

    // Calculate cols count
    this.cols = index === 0 || !this.cols || this.cols % totalCols === 0 ? 0 : this.cols
    const cols = sizeRatio > 1.5 && totalCols - this.cols >= 2 ? 2 : 1
    this.cols += cols

    return (
      <Fade in key={id}>
        <GridListTile
          cols={cols}
          className={classes.result}
        >
          <img // eslint-disable-line
            alt=""
            tabIndex="-1"
            src={urls.regular}
            className={classes.resultImage}
            onClick={this.handleResultClick(result)}
          />
          <GridListTileBar
            className={classes.resultBar}
            title={(
              <Link
                external
                target="_blank"
                to={`${user.links.html}?utm_source=indemandly&utm_medium=referral`}
              >
                {'by '}
                { user.name }
              </Link>
            )}
          />
        </GridListTile>
      </Fade>
    )
  }

  renderResults() {
    const { results } = this.state

    if (!results.length) {
      return this.renderEmptyResult()
    }

    return (
      <Fragment>
        <Spacer />
        <InfiniteScroll
          hasMore={this.hasMore}
          loadMore={this.loadMore}
        >
          <GridList cols={this.getResultColsCount()} cellHeight={220}>
            { results.map(this.renderResult) }
          </GridList>
        </InfiniteScroll>
      </Fragment>
    )
  }

  renderError() {
    const { unsplashSearchAction } = this.props
    return (
      <Fragment>
        <Spacer />
        <DesktopOnlyCard>
          <List>
            <ListItem>
              <ListItemIcon>
                <ErrorIcon color="error" />
              </ListItemIcon>
              <ListItemText
                disableTypography
                primary={(
                  <Typography variant="body1" color="error">
                    { unsplashSearchAction.error.message }
                  </Typography>
                )}
              />
            </ListItem>
          </List>
        </DesktopOnlyCard>
      </Fragment>
    )
  }

  render() {
    const { onCancel, unsplashSearchAction, } = this.props
    const { results } = this.state
    return (
      <Container>
        <Expand expanded onToggle={onCancel}>
          { this.renderInput() }
        </Expand>
        { !results && unsplashSearchAction.isFailed && this.renderError() }
        { !!results && this.renderResults() }
        { unsplashSearchAction.isPending && this.renderLoading() }
      </Container>
    )
  }
}
